import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { ClipboardList, Plus, ArrowRight, CalendarClock, Loader2, Download } from 'lucide-react';
import { toast } from 'sonner';

export default function Inspections() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isManager = ['admin', 'manager'].includes(user.role);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get('/inspections').then((r) => { setList(r.data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const PART_SIZE = 40; // proofs per ZIP part
  const [zipJob, setZipJob] = useState(null); // {name, part, parts, pct, status}
  const [zipCfg, setZipCfg] = useState(null); // {insp, organize, scope} — the options dialog

  const triggerDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  };

  const runZip = async (i, organize, scope) => {
    setZipCfg(null);
    const baseName = (i.name || 'inspection').replace(/[^A-Za-z0-9 _.-]+/g, '_');
    setZipJob({ name: baseName, part: 0, parts: 1, pct: 0, status: 'preparing' });
    try {
      const info = (await api.get(`/inspections/${i.id}/photos-info?scope=${scope}`)).data;
      const proofs = info.proofs || 0;
      if (!proofs) { setZipJob(null); toast.error('No photos captured yet for this option'); return; }
      const parts = Math.max(1, Math.ceil(proofs / PART_SIZE));
      for (let p = 0; p < parts; p++) {
        setZipJob({ name: baseName, part: p + 1, parts, pct: 0, status: 'downloading', photos: info.photos });
        const paging = parts > 1 ? `&skip=${p * PART_SIZE}&limit=${PART_SIZE}` : '';
        const res = await api.get(`/inspections/${i.id}/photos-zip?organize=${organize}&scope=${scope}${paging}`, {
          responseType: 'blob',
          onDownloadProgress: (e) => {
            const pct = e.total ? Math.round((e.loaded / e.total) * 100) : Math.min(95, Math.round(e.loaded / 50000));
            setZipJob((j) => (j ? { ...j, pct } : j));
          },
        });
        const suffix = parts > 1 ? `-part${p + 1}of${parts}` : '';
        triggerDownload(res.data, `${baseName}-photos${suffix}.zip`);
      }
      setZipJob(null);
      toast.success(parts > 1 ? `Downloaded ${parts} ZIP parts` : 'ZIP downloaded');
    } catch (err) {
      setZipJob(null);
      toast.error(err?.response?.status === 404 ? 'No photos captured yet for this option' : 'Download failed — please try again');
    }
  };

  const toggleStatus = async (i) => {
    const next = i.status === 'closed' ? 'assigned' : 'closed';
    try {
      await api.patch(`/inspections/${i.id}/status`, { status: next });
      toast.success(next === 'closed' ? 'Inspection deactivated — hidden from operator' : 'Inspection reactivated');
      load();
    } catch { toast.error('Could not update inspection status'); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="overline text-primary">Work Assignment</p>
          <h1 className="font-head text-3xl font-bold">Inspections</h1>
        </div>
        {isManager && <CreateInspection onCreated={load} />}
      </div>

      {loading ? <Loader2 className="h-6 w-6 animate-spin text-primary" /> : list.length === 0 ? (
        <div className="rounded-xl border border-border bg-card p-10 text-center text-muted-foreground">No inspections {isManager ? 'created' : 'assigned'} yet.</div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {list.map((i) => (
            <div key={i.id} data-testid={`inspection-card-${i.id}`} className={`rounded-xl border bg-card p-4 ${i.status === 'closed' ? 'border-red-900/50 opacity-70' : 'border-border'}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-head text-lg font-bold">{i.name || i.society_name}</p>
                  <p className="text-sm text-muted-foreground">{i.society_name}{i.campaign_name ? ` · ${i.campaign_name}` : ''}</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {i.kind === 'deinstall' && <span className="rounded-full border border-amber-600 bg-amber-950/50 px-2 py-0.5 text-[9px] font-mono uppercase text-amber-300">Deinstallation</span>}
                  {i.status === 'closed' ? (
                    <span className="rounded-full border border-red-600 bg-red-950/50 px-2 py-0.5 text-[10px] font-mono uppercase text-red-300" data-testid={`inspection-closed-badge-${i.id}`}>Closed</span>
                  ) : (
                    <span className="rounded-full border border-sky-600 bg-sky-950/50 px-2 py-0.5 text-[10px] font-mono uppercase text-sky-300" data-testid={`inspection-active-badge-${i.id}`}>{i.status}</span>
                  )}
                </div>
              </div>
              <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><ClipboardList className="h-3.5 w-3.5" /> {i.site_ids.length} sites</span>
                <span>Operators: {((i.operator_names && i.operator_names.length ? i.operator_names : [i.operator_name]).filter(Boolean)).join(', ') || '—'}</span>
                {i.due_date && <span className="flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" /> {i.due_date.slice(0, 10)}</span>}
              </div>
              <Button size="sm" className="mt-3 w-full" data-testid={`open-inspection-${i.id}`}
                onClick={() => navigate(`/inspections/${i.id}`)}>
                Open Inspection <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
              {isManager && (
                <Button size="sm" variant="outline" data-testid={`toggle-inspection-${i.id}`}
                  className={`mt-2 w-full ${i.status === 'closed' ? 'border-emerald-700 text-emerald-400 hover:bg-emerald-950/40' : 'border-red-800 text-red-400 hover:bg-red-950/40'}`}
                  onClick={() => toggleStatus(i)}>
                  {i.status === 'closed' ? 'Reactivate (show to operator)' : 'Deactivate (hide from operator)'}
                </Button>
              )}
              {isManager && (
                <Button size="sm" variant="outline" data-testid={`zip-btn-${i.id}`} className="mt-2 w-full border-sky-800 text-sky-400 hover:bg-sky-950/40"
                  onClick={() => setZipCfg({ insp: i, organize: 'location', scope: 'round' })}>
                  <Download className="mr-1 h-4 w-4" /> Download Photos
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!zipCfg} onOpenChange={(v) => !v && setZipCfg(null)}>
        <DialogContent className="max-w-md" data-testid="zip-options-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Download className="h-5 w-5 text-sky-500" /> Download inspection photos</DialogTitle>
            <DialogDescription>Every capture is included (all photos, not just the latest). Choose how to organise the ZIP.</DialogDescription>
          </DialogHeader>
          {zipCfg && (
            <div className="space-y-4 py-1">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Organise by</Label>
                <div className="mt-1.5 grid gap-2">
                  {[
                    ['location', 'Society › Tower › Lift › Wall folders', 'Foldered by physical location + details.csv'],
                    ['condition', 'Condition (Damaged / Repaired / …)', 'Foldered by status + details.csv'],
                    ['flat', 'All photos in one folder', 'Flat photos/ folder, no CSV'],
                  ].map(([val, title, sub]) => (
                    <button key={val} data-testid={`zip-org-${val}`} onClick={() => setZipCfg((c) => ({ ...c, organize: val }))}
                      className={`rounded-lg border-2 px-3 py-2 text-left transition ${zipCfg.organize === val ? 'border-sky-500 bg-sky-50' : 'border-border hover:border-sky-300'}`}>
                      <p className="text-sm font-bold text-slate-800">{title}</p>
                      <p className="text-xs text-muted-foreground">{sub}</p>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Which photos</Label>
                <div className="mt-1.5 grid gap-2">
                  {[
                    ['round', 'This round only', "Just the captures made in this inspection"],
                    ['site', 'All photos on these walls', 'Full history ever taken on these sites'],
                  ].map(([val, title, sub]) => (
                    <button key={val} data-testid={`zip-scope-${val}`} onClick={() => setZipCfg((c) => ({ ...c, scope: val }))}
                      className={`rounded-lg border-2 px-3 py-2 text-left transition ${zipCfg.scope === val ? 'border-emerald-500 bg-emerald-50' : 'border-border hover:border-emerald-300'}`}>
                      <p className="text-sm font-bold text-slate-800">{title}</p>
                      <p className="text-xs text-muted-foreground">{sub}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setZipCfg(null)}>Cancel</Button>
            <Button data-testid="zip-generate-btn" onClick={() => runZip(zipCfg.insp, zipCfg.organize, zipCfg.scope)} className="bg-sky-600 hover:bg-sky-500">
              <Download className="mr-1 h-4 w-4" /> Generate ZIP
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!zipJob} onOpenChange={() => {}}>
        <DialogContent className="max-w-sm" data-testid="zip-progress-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Download className="h-5 w-5 text-sky-500" /> Preparing photos ZIP</DialogTitle>
            <DialogDescription>
              {zipJob?.status === 'preparing'
                ? 'Checking how many photos to package…'
                : zipJob?.parts > 1
                  ? `Large inspection — downloading in ${zipJob.parts} parts. Part ${zipJob.part} of ${zipJob.parts}.`
                  : 'Packaging your photos. This may take a moment for large inspections.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-secondary">
              <div className="h-full rounded-full bg-sky-500 transition-all" style={{ width: `${zipJob?.pct || 5}%` }} data-testid="zip-progress-bar" />
            </div>
            <p className="text-center text-xs text-muted-foreground">{zipJob?.status === 'preparing' ? '' : `${zipJob?.pct || 0}%`}{zipJob?.parts > 1 ? ` · part ${zipJob?.part}/${zipJob?.parts}` : ''}</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CreateInspection({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [societies, setSocieties] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [operators, setOperators] = useState([]);
  const [clients, setClients] = useState([]);
  const [campIds, setCampIds] = useState([]);
  const [deinstallIds, setDeinstallIds] = useState([]);
  const [markMap, setMarkMap] = useState({});
  const [untickedAvail, setUntickedAvail] = useState([]);
  const [replaceMap, setReplaceMap] = useState({});
  const [form, setForm] = useState({ name: '', campaign_id: '', due_date: '', scope: 'single' });
  const [opIds, setOpIds] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get('/societies').then((r) => setSocieties(r.data));
    api.get('/campaigns').then((r) => setCampaigns(r.data));
    api.get('/users?role=operator').then((r) => setOperators(r.data));
    api.get('/users?role=client').then((r) => setClients(r.data)).catch(() => setClients([]));
  }, [open]);

  const create = async () => {
    const cids = form.scope === 'multi' ? campIds : (form.campaign_id ? [form.campaign_id] : []);
    if (!cids.length) return toast.error(form.scope === 'multi' ? 'Select at least one campaign' : 'Select a campaign');
    if (!opIds.length) return toast.error('At least one operator is required');
    if (!form.due_date) return toast.error('Due date is required');
    const selCamps = campaigns.filter((c) => cids.includes(c.id));
    const socIds = [...new Set(selCamps.flatMap((c) => c.society_ids || []))];
    const installIds = cids.filter((cid) => markMap[cid] === 'install');
    const deinstallAll = [...new Set([...cids.filter((cid) => markMap[cid] === 'deinstall'), ...deinstallIds])];
    const mode = installIds.length ? 'install' : 'inspect';
    setSaving(true);
    try {
      await api.post('/inspections', {
        name: form.name || null,
        society_id: socIds[0] || null, society_ids: socIds,
        campaign_id: cids[0] || null, campaign_ids: cids, operator_id: opIds[0] || null, operator_ids: opIds, site_ids: [],
        install_campaign_ids: installIds, deinstall_campaign_ids: deinstallAll,
        available_campaign_ids: availCampIds, available_client_ids: availClientIds, replace_map: replaceMap,
        due_date: form.due_date, mode, scope: form.scope,
      });
      toast.success('Inspection created & assigned');
      setOpen(false); setForm({ name: '', campaign_id: '', due_date: '', scope: 'single' }); setOpIds([]); setCampIds([]); setDeinstallIds([]); setMarkMap({}); setUntickedAvail([]); setReplaceMap({});
      onCreated();
    } catch (e) { toast.error('Failed to create'); }
    setSaving(false);
  };

  const selCids = form.scope === 'multi' ? campIds : (form.campaign_id ? [form.campaign_id] : []);
  const selCamps = campaigns.filter((c) => selCids.includes(c.id));
  const derivedSocIds = [...new Set(selCamps.flatMap((c) => c.society_ids || []))];
  const derivedSocieties = derivedSocIds.map((sid) => (societies.find((s) => s.id === sid) || {}).name).filter(Boolean);
  const inRoundIds = [...new Set([...selCids, ...selCids.filter((cid) => markMap[cid] === 'install'), ...selCids.filter((cid) => markMap[cid] === 'deinstall'), ...deinstallIds])];
  // ALL clients from the client master + campaign-only client names — even ones with no campaign
  const allClients = (() => {
    const list = clients.filter((c) => c.active !== false).map((c) => ({ id: c.id, name: c.name }));
    const known = new Set(list.map((c) => c.name));
    campaigns.forEach((c) => { const cn = c.client_name; if (cn && !known.has(cn)) { list.push({ id: `name:${cn}`, name: cn }); known.add(cn); } });
    return list.sort((a, b) => String(a.name).localeCompare(String(b.name)));
  })();
  const availClientIds = allClients.map((c) => c.id).filter((x) => !untickedAvail.includes(x));
  const availCampIds = campaigns.filter((c) => availClientIds.includes(c.client_user_id) || availClientIds.includes(`name:${c.client_name}`)).map((c) => c.id);
  const inRoundClientIds = campaigns.filter((x) => inRoundIds.includes(x.id)).map((x) => x.client_user_id || `name:${x.client_name}`);
  const toggleAvail = (cid) => setUntickedAvail((p) => p.includes(cid) ? p.filter((x) => x !== cid) : [...p, cid]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="new-inspection-btn"><Plus className="mr-1 h-4 w-4" /> New Inspection</Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-head text-2xl">Create Inspection</DialogTitle><DialogDescription>Pick a single or group inspection, choose the campaign(s), operator and due date. Then mark what the operator does for each client: Check, Install or Deinstall.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Inspection name</Label>
            <Input data-testid="insp-name" placeholder="e.g. Skyline Sept round" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Inspection type</Label>
              <Select value={form.scope} onValueChange={(v) => { setForm({ ...form, scope: v, campaign_id: '' }); setCampIds([]); setMarkMap({}); }}>
                <SelectTrigger data-testid="insp-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">Single (one campaign)</SelectItem>
                  <SelectItem value="multi">Group (multiple campaigns)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Operator(s) <span className="text-muted-foreground">(tick all who may work this round — any can capture)</span></Label>
              <div className="mt-1 max-h-28 space-y-1 overflow-y-auto rounded-lg border border-border p-2" data-testid="insp-operators">
                {operators.map((o) => (
                  <label key={o.id} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={opIds.includes(o.id)} data-testid={`insp-op-${o.id}`} onCheckedChange={() => setOpIds((p) => p.includes(o.id) ? p.filter((x) => x !== o.id) : [...p, o.id])} />
                    {o.name}
                  </label>
                ))}
                {!operators.length && <p className="text-xs text-muted-foreground">No operators yet.</p>}
              </div>
            </div>
            <div className="col-span-2">
              <Label>{form.scope === 'multi' ? 'Campaigns' : 'Campaign'}</Label>
              {form.scope === 'multi' ? (
                <div className="max-h-28 space-y-1 overflow-y-auto rounded-lg border border-border p-2" data-testid="insp-campaigns-multi">
                  {campaigns.map((c) => (
                    <label key={c.id} className="flex items-center gap-2 text-sm">
                      <Checkbox checked={campIds.includes(c.id)} data-testid={`insp-campaign-${c.id}`} onCheckedChange={() => setCampIds((p) => p.includes(c.id) ? p.filter((x) => x !== c.id) : [...p, c.id])} />
                      {c.name} {c.client_name ? <span className="text-xs text-muted-foreground">· {c.client_name}</span> : null}
                    </label>
                  ))}
                  {!campaigns.length && <p className="text-xs text-muted-foreground">No campaigns yet.</p>}
                </div>
              ) : (
                <Select value={form.campaign_id} onValueChange={(v) => setForm({ ...form, campaign_id: v })}>
                  <SelectTrigger data-testid="insp-campaign"><SelectValue placeholder="Campaign" /></SelectTrigger>
                  <SelectContent>{campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.client_name ? ` · ${c.client_name}` : ''}</SelectItem>)}</SelectContent>
                </Select>
              )}
              {derivedSocieties.length > 0 && (
                <p className="mt-1 text-xs text-muted-foreground" data-testid="insp-derived-societies">
                  Societies (from campaign): <span className="text-foreground">{derivedSocieties.join(', ')}</span> · all mapped sites will be inspected
                </p>
              )}
            </div>
            <div>
              <Label>Due date <span className="text-rose-400">*</span></Label>
              <Input type="date" data-testid="insp-due" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
            </div>
          </div>
          {selCamps.length > 0 && (
            <div>
              <Label>Job per client <span className="text-muted-foreground">(what the operator does for each client)</span></Label>
              <div className="mt-1 space-y-1.5" data-testid="insp-job-marks">
                {selCamps.map((c) => {
                  const cur = markMap[c.id] || 'check';
                  return (
                    <div key={c.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-border p-2">
                      <span className="min-w-0 flex-1 truncate text-sm font-medium">{c.name}{c.client_name ? ` · ${c.client_name}` : ''}</span>
                      <div className="flex gap-1">
                        {[['check', 'Check'], ['install', 'Install'], ['deinstall', 'Deinstall']].map(([v, l]) => {
                          const on = cur === v;
                          const cls = !on ? 'border-border text-muted-foreground hover:border-primary/50'
                            : v === 'install' ? 'border-blue-500 bg-blue-500 text-white'
                            : v === 'deinstall' ? 'border-amber-500 bg-amber-500 text-white'
                            : 'border-emerald-500 bg-emerald-500 text-white';
                          return (
                            <button key={v} type="button" data-testid={`insp-mark-${v}-${c.id}`} onClick={() => setMarkMap((m) => ({ ...m, [c.id]: v }))}
                              className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${cls}`}>{l}</button>
                          );
                        })}
                      </div>
                      {cur === 'install' && (
                        <div className="mt-1.5 flex w-full items-center gap-2">
                          <span className="shrink-0 text-[11px] text-muted-foreground">Replacing:</span>
                          <Select value={replaceMap[c.id] || 'auto'} onValueChange={(v) => setReplaceMap((m) => ({ ...m, [c.id]: v === 'auto' ? '' : v }))}>
                            <SelectTrigger data-testid={`insp-replacing-${c.id}`} className="h-8 flex-1 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="auto">Auto — whoever's board is on the wall</SelectItem>
                              {campaigns.filter((x) => x.id !== c.id).map((x) => <SelectItem key={x.id} value={x.id}>{x.client_name || x.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div>
            <Label>Clients the operator can select <span className="text-muted-foreground">(all pre-ticked — untick to hide a client from the operator)</span></Label>
            <div className="mt-1 max-h-40 space-y-1 overflow-y-auto rounded-lg border border-border p-2" data-testid="insp-available-clients">
              {allClients.map((c) => {
                const hasCampaign = campaigns.some((x) => x.client_user_id === c.id || x.client_name === c.name);
                return (
                <label key={c.id} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={availClientIds.includes(c.id)} data-testid={`insp-avail-${c.id}`} onCheckedChange={() => toggleAvail(c.id)} />
                  <span className="font-medium">{c.name}</span>
                  {inRoundClientIds.includes(c.id) && <span className="rounded-full border border-sky-600 px-1.5 text-[9px] uppercase text-sky-600">in round</span>}
                  {!hasCampaign && <span className="rounded-full border border-slate-500 px-1.5 text-[9px] uppercase text-slate-400">no campaign</span>}
                </label>
                );
              })}
              {!allClients.length && <p className="text-xs text-muted-foreground">No clients yet.</p>}
            </div>
            <p className="mt-1 text-xs text-muted-foreground" data-testid="insp-avail-count">{availClientIds.length} client(s) will appear on the operator's capture screen.</p>
          </div>
          <div>
            <Label>Also deinstall old client <span className="text-muted-foreground">(optional — old board to remove whose campaign is not in this round)</span></Label>
            <Select value={deinstallIds[0] || 'none'} onValueChange={(v) => setDeinstallIds(v === 'none' ? [] : [v])}>
              <SelectTrigger data-testid="insp-deinstall-extra"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {campaigns.filter((c) => !selCids.includes(c.id)).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.client_name ? ` · ${c.client_name}` : ''}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><Button onClick={create} disabled={saving} data-testid="save-inspection-btn">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create & Assign</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
